import { Y42FeatureType } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { GitFolderToUI, Y42GitFolders } from '../git';
import { AbstractFeatureStoreModel } from '../models';
import { Y42Routes, Y42RouteToFeatureType } from '../routing';
import { Y42RouterService } from '../routing/y42-router.service';

export interface ServiceDefinition<Service, Query> {
  service: Service;
  query: Query;
  children?: Array<ServiceDefinition<Service, Query>>;
}

export interface ServiceDefinitionWithName<Service, Query> extends ServiceDefinition<Service, Query> {
  featureType: Y42FeatureType;
}

export abstract class AbstractFeatureAggregationService<Service, Query> {
  constructor(protected readonly routerService: Y42RouterService) {}

  abstract readonly featureTypeToServiceDefinition: Record<Y42FeatureType, ServiceDefinition<unknown, unknown> | null>;

  getAllServiceDefinitions(): Array<ServiceDefinitionWithName<Service, Query>> {
    return Object.entries(this.featureTypeToServiceDefinition)
      .filter(([_, def]) => isNotNullish(def))
      .map(([featureType, definition]) => ({
        featureType: featureType as Y42FeatureType,
        ...(definition as ServiceDefinition<Service, Query>),
      }));
  }

  getByFeatureType<T extends Y42FeatureType>(featureType: T): ServiceDefinition<Service, Query> | undefined {
    const services = this.featureTypeToServiceDefinition[featureType];
    const ret = services ? { featureType, ...services } : undefined;
    return ret as ServiceDefinition<Service, Query>;
  }

  getByRoute<T extends Y42Routes>(featureRoute: T) {
    const featureType = Y42RouteToFeatureType[featureRoute];

    if (!featureType) {
      return;
    }

    const nonNullableFeatureType = featureType!; // necessary for `typeof` to work
    return this.getByFeatureType<typeof nonNullableFeatureType>(nonNullableFeatureType);
  }

  getByGitPath(path: AbstractFeatureStoreModel['$path']) {
    // sort git-folders by number of "/" in path, higher count first
    const gitFolders = Object.values(Y42GitFolders).sort((a, b) => b.split('/').length - a.split('/').length);
    const gitFolder = gitFolders.find((folder) => path.includes(folder));

    if (!gitFolder) {
      return;
    }

    const featureType = GitFolderToUI[gitFolder].name;
    return this.getByFeatureType(featureType) as ServiceDefinitionWithName<Service, Query>;
  }

  getActiveFeatureName(): Y42FeatureType | undefined {
    const featureRoute = this.routerService.getFeatureRouteFromURL();
    return Y42RouteToFeatureType[featureRoute] || undefined;
  }

  getActiveFeatureFromURL(url?: string) {
    const featureRoute = this.routerService.getFeatureRouteFromURL(url);
    const featureServiceDef = this.getByRoute(featureRoute);
    return featureServiceDef as ServiceDefinitionWithName<Service, Query> | undefined;
  }
}
